import { Component, OnInit, HostListener } from '@angular/core';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { VesselService } from '../../../../../../services/vessel.service';
import { UserProfileService } from '../../../../../../services/user-profile.service';
import { AuthService } from '../../../../../../core/auth/auth.service';
import { TourAnchorDirective } from '../../../../../../features/learning-center/engine/tour-anchor.directive';
import { depthFromMeters, speedFromMs, tempFromC } from '../../../../../../shared/utils/unit-formatter.util';
import { formatLatLonDecimalMinutes } from '../../../../../../shared/utils/coordinate-format.util';

/** Read-only vessel view: no create/edit/delete/borrow actions (those live in the mobile app). */
@Component({
  selector: 'app-vessels',
  standalone: true,
  imports: [CommonModule, MatIconModule, TourAnchorDirective],
  templateUrl: './vessels.component.html',
  styleUrl: './vessels.component.scss',
})
export class VesselsComponent implements OnInit {
  vessels: any[] = [];
  selectedVessel: any = null;
  // True from the start: the settings fetch runs before fetchVessels(), and with false the "no vessels" state flashed in that gap.
  loading = true;
  currentImageIndex = 0;

  // User unit preferences
  private depthUnit = 'feet';
  private speedUnit = 'knots';
  private tempUnit = 'fahrenheit';

  // Live Snapshot state
  snapshotLoading = false;
  snapshotAvailable = false;
  // Every value is pre-formatted for display — a missing reading is the string
  // '--', never 0. See `parseObservation`.
  snapshot: {
    cogDeg: string;
    sogValue: string;
    sogUnit: string;
    waterTempValue: string;
    waterTempUnit: string;
    waterDepthValue: string;
    waterDepthUnit: string;
    batteryV: string;
    lastUpdated: string;
    coordsDisplay: string;
  } | null = null;

  // ── Borrow feature state (read-only) ──
  activeTab: 'my-vessel' | 'requests' | 'sent-requests' = 'my-vessel';
  borrowStatus: any = null;
  borrowerDisplayName = '';
  incomingRequests: any[] = [];
  sentRequests: any[] = [];
  incomingRequestsCount = 0;
  showVesselDropdown = false;

  constructor(
    private vesselService: VesselService,
    private userProfileService: UserProfileService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.loadUserSettingsThenFetch();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.vessel-dropdown')) {
      this.showVesselDropdown = false;
    }
  }

  loadUserSettingsThenFetch(): void {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) {
      this.fetchVessels();
      return;
    }
    this.userProfileService.getProfileById(userId).pipe(
      finalize(() => this.fetchVessels())
    ).subscribe({
      next: (res: any) => {
        const s = res?.settings ?? {};
        this.depthUnit = this.normalizeDepthUnit(s.depth_unit);
        this.speedUnit = this.normalizeSpeedUnit(s.speed_unit);
        this.tempUnit = this.normalizeTempUnit(s.temperature_unit);
      },
    });
  }

  private normalizeDepthUnit(u: string | undefined): string {
    const v = (u ?? '').trim().toLowerCase();
    if (v === 'fathoms' || v === 'fathom' || v === 'fm') return 'fathoms';
    if (v === 'meters' || v === 'meter' || v === 'm') return 'meters';
    return 'feet';
  }

  private normalizeSpeedUnit(u: string | undefined): string {
    const v = (u ?? '').trim().toLowerCase();
    if (v === 'mph') return 'mph';
    if (v === 'kmh' || v === 'km/h' || v === 'kph') return 'kmh';
    return 'knots';
  }

  private normalizeTempUnit(u: string | undefined): string {
    const v = (u ?? '').trim().toLowerCase();
    if (v === 'celsius' || v === 'c') return 'celsius';
    return 'fahrenheit';
  }

  fetchVessels(): void {
    this.loading = true;
    this.vesselService.getVessels().subscribe({
      next: (data: any) => {
        this.vessels = data.results || [];
        if (this.vessels.length > 0) {
          const primary = this.vessels.find((v: any) => v.is_primary);
          this.selectedVessel = primary || this.vessels[0];
          this.loadSnapshot(this.selectedVessel._id);
          this.loadBorrowStatus(this.selectedVessel._id);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error fetching vessels:', error);
        this.loading = false;
      },
    });
  }

  // ── Snapshot helpers ──

  loadSnapshot(vesselId: string): void {
    this.snapshotLoading = true;
    this.snapshotAvailable = false;
    this.snapshot = null;

    this.vesselService.getLatestObservation(vesselId).subscribe({
      next: (obs: any) => {
        if (obs?.message) {
          this.snapshotAvailable = false;
          this.snapshotLoading = false;
          return;
        }
        this.snapshot = this.parseObservation(obs);
        this.snapshotAvailable = true;
        this.snapshotLoading = false;
      },
      error: () => {
        this.snapshotAvailable = false;
        this.snapshotLoading = false;
      },
    });
  }

  private parseObservation(obs: any) {
    const pgn = (name: string) =>
      obs.pgns?.find((p: any) => p.name === name)?.fields ?? {};

    const pos = pgn('GNSSPositionData');
    const cogsog = pgn('COGSOGRapidUpdate');
    const depth = pgn('WaterDepth');
    const env = pgn('EnvironmentalParameters');
    const batt = pgn('BatteryStatus');
    const ts: string = obs.pgns?.[0]?.ts_utc ?? '';

    // A reading the vessel never reported is *absent*, not zero. These used to
    // be `?? 0`, so a vessel with no telemetry showed a confident "0 kts /
    // 32°F / 0 ft / 0.00 V" instead of the app's "--". Every formatter below
    // takes null through to the '--' placeholder, exactly like the app's
    // vessels_tab_view (which resolves each field independently and prints
    // '--' for the ones that are missing).
    const num = (value: unknown): number | null => {
      if (value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const sogMs = num(cogsog.sog);
    const cogDegRaw = num(cogsog.cog_deg);
    const tempC = num(env.temp_c);
    const depthM = num(depth.depth_m);
    const voltage = num(batt.voltage_v);
    const lat = num(pos.lat_deg);
    const lon = num(pos.lon_deg);

    // Decimal places match the app: speed 1, temperature 0, depth 0, volts 2.
    const sog = speedFromMs(sogMs, this.speedUnit, 1, '--');
    const temp = tempFromC(tempC, this.tempUnit, 0, '--');
    const waterDepth = depthFromMeters(depthM, this.depthUnit, 0, '--');

    return {
      cogDeg: cogDegRaw === null ? '--' : String(Math.round(cogDegRaw)),
      sogValue: sog.value,
      sogUnit: sog.unit,
      waterTempValue: temp.value,
      waterTempUnit: temp.unit,
      waterDepthValue: waterDepth.value,
      waterDepthUnit: waterDepth.unit,
      batteryV: voltage === null ? '--' : voltage.toFixed(2),
      lastUpdated: ts
        ? new Date(ts + 'Z').toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
        : '--',
      coordsDisplay: formatLatLonDecimalMinutes(lat, lon, '--'),
    };
  }

  // ── Selection & Carousel Logic ──

  selectVessel(vessel: any): void {
    this.selectedVessel = vessel;
    this.currentImageIndex = 0;
    this.loadSnapshot(vessel._id);
    this.loadBorrowStatus(vessel._id);
    this.showVesselDropdown = false;
  }

  toggleVesselDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showVesselDropdown = !this.showVesselDropdown;
  }

  onImageScroll(el: HTMLElement): void {
    this.currentImageIndex = Math.round(el.scrollLeft / el.clientWidth);
  }

  // ── Borrow status + request lists (read-only) ──

  switchTab(tab: 'my-vessel' | 'requests' | 'sent-requests'): void {
    this.activeTab = tab;
    if (tab === 'requests') {
      this.loadIncomingRequests();
    } else if (tab === 'sent-requests') {
      this.loadSentRequests();
    }
  }

  loadBorrowStatus(vesselId: string): void {
    if (!vesselId) return;
    this.borrowStatus = null;
    this.borrowerDisplayName = '';

    this.vesselService.getBorrowStatus(vesselId).subscribe({
      next: (status: any) => {
        this.borrowStatus = status;
        if (status?.status === 'lent_out' && status?.borrower_ref?.identifier) {
          this.loadBorrowerProfile(status.borrower_ref.identifier);
        }
      },
    });
  }

  private loadBorrowerProfile(profileId: string): void {
    this.userProfileService.getProfileById(profileId).subscribe({
      next: (profile: any) => {
        this.borrowerDisplayName = this.getDisplayName(profile);
      },
      error: () => {
        this.borrowerDisplayName = '';
      },
    });
  }

  loadIncomingRequests(): void {
    this.loading = true;
    this.vesselService.getBorrowRequests().subscribe({
      next: (data: any) => {
        this.incomingRequests = data?.results ?? [];
        this.incomingRequestsCount = data?.count ?? this.incomingRequests.length;
        this.loading = false;
      },
      error: () => {
        this.incomingRequests = [];
        this.loading = false;
      },
    });
  }

  loadSentRequests(): void {
    this.loading = true;
    this.vesselService.getMyBorrowRequests().subscribe({
      next: (data: any) => {
        this.sentRequests = data?.results ?? [];
        this.loading = false;
      },
      error: () => {
        this.sentRequests = [];
        this.loading = false;
      },
    });
  }

  get isVesselBorrowed(): boolean {
    return this.selectedVessel?.is_borrowed === true || this.borrowStatus?.status === 'borrowed';
  }

  get isVesselLentOut(): boolean {
    return this.selectedVessel?.is_lent_out === true || this.borrowStatus?.status === 'lent_out';
  }

  private normalizeStatus(req: any): string {
    return (req?.status ?? '').trim().toLowerCase().replace(/[-\s]/g, '_');
  }

  isWaitlisted(req: any): boolean {
    const status = this.normalizeStatus(req);
    return status === 'waitlisted' || status === 'unavailable';
  }

  isForceEnded(req: any): boolean {
    const status = this.normalizeStatus(req);
    return status === 'force_ended' || status === 'force_end' || status === 'revoked' || status === 'stopped';
  }

  isReturned(req: any): boolean {
    const status = this.normalizeStatus(req);
    return status === 'returned' || status === 'completed' || status === 'ended';
  }

  /** Status label shown on request rows (read-only; replaces the old action buttons). */
  requestStatusLabel(req: any): string {
    if (this.isWaitlisted(req)) return 'Waitlisted';
    if (this.isForceEnded(req)) return 'Access ended';
    if (this.isReturned(req)) return 'Returned';
    return 'Pending';
  }

  timeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${diffWeeks}w ago`;
  }

  getDisplayName(profile: any): string {
    if (!profile) return 'Unknown';
    if (profile.display_name) return profile.display_name;
    const first = profile.first_name || '';
    const last = profile.last_name || '';
    const full = `${first} ${last}`.trim();
    return full || profile.email_address || 'Unknown';
  }

  formatBadgeCount(count: number): string {
    if (count <= 0) return '';
    if (count < 10) return `0${count}`;
    if (count > 99) return '99+';
    return `${count}`;
  }
}
